import { Fragment, useEffect, useRef, useState } from 'react';
import {
  ChevronDown, ChevronLeft, ChevronRight, Copy, HeartPulse, History, Layers, Lightbulb, Minus, MoreHorizontal, Plus, Repeat,
  RotateCcw, StickyNote,
  Target, Trash2, Trophy, Wrench,
} from 'lucide-react';
import {
  EXERCISE_LIBRARY, dateKey, getTipsForExercise, parseAnchor, uid, workoutToday,
} from './state';
import {
  countCompletedSets, daysBetweenKeys, findPreviousSession, formatSetShape,
  sessionVolume, sumDayVolume,
} from './workout';
import { checkSetPr } from './logic/records';
import { resolveMuscles } from './logic/muscles';
import { activeProgram } from './logic/progression';
import { liftTrend } from './logic/trends';
import {
  addSetTo, firstOpenRow, isBodyweight, loadStep, nextUnfinished, patchSet, placeholderFor, plannedCount,
  readSet, removeSetFrom, resolveDone, restoreExercise, snapshotExercise, tickSet, untickSet, upNextSet, withRir,
  writeSet, isSkipped, skipExercise, unskipExercise,
} from './setLog';
import { restSecondsFor } from './rest';
import {
  cardTarget, clearPrescription, fillFromTarget, fmtKg, isStraight, repRangeLabel, stallChip, targetShape,
} from './targets';
import { addDays, planDaysWith } from './programs';
import { ProgramSheet, SetChips } from './ProgramSheet';
import { unlockAudio } from './restAlerts';
import { promoteSwap, swapNudge } from './swaps';
import { SwapNudge, SwapSheet, nudgeSnoozed, snoozeNudge } from './SwapSheet';
import {
  ActiveSetPanel, CompactSetRow, RirStrip, SetMenuSheet, SetTableHeader, UndoSnackbar,
} from './SetRows';
import { FONT_BODY, FONT_MONO } from './theme';
import { Button, Card, StatTile, TypeDot } from './ui';
import { ExercisePicker, makeCustomExerciseHandlers } from './ExercisePicker';
import {
  chooseDay, dayFor, lastSameDay, nextTrainingDay, plannedProgress, prsOn, sessionMinutes, setCheckin,
  endDeload, setSessionFields, startDeload, stampSession,
} from './session';
import { CheckinCard, FinishCard, WorkoutBar } from './SessionCards';
import { DeloadBanner, DeloadStarter } from './DeloadCard';
import { deloadOn, trainingLogs } from './logic/deload';

/* Form cues open by themselves only the first time an exercise is opened on
 * this device; after that the set table is visible without scrolling. */
const TIPS_SEEN_KEY = 'xceller_fit_tips_seen';
function readTipsSeen() {
  try {
    const v = JSON.parse(localStorage.getItem(TIPS_SEEN_KEY) || '{}');
    return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
  } catch { return {}; }
}
function markTipsSeen(name) {
  try {
    const seen = readTipsSeen();
    const k = name.toLowerCase();
    if (seen[k]) return;
    localStorage.setItem(TIPS_SEEN_KEY, JSON.stringify({ ...seen, [k]: 1 }));
  } catch {}
}

/* The picker's muscle filter for an exercise ('Back' for a row), or 'all'. */
function libraryMuscle(name) {
  const match = resolveMuscles(name)?.match;
  return EXERCISE_LIBRARY.find(e => e.name === match)?.muscle || 'all';
}

/* PR toast lines for a records.checkSetPr event. */
function prLines(pr) {
  if (pr.type === 'reps') return { title: 'NEW REP PR', prev: `prev ${pr.prev.weight}kg × ${pr.prev.reps}` };
  if (pr.type === 'e1rm') {
    return { title: 'NEW e1RM PR', prev: `e1RM ${pr.e1rm}kg${pr.e1rmGain > 0 ? ` (+${pr.e1rmGain})` : ''}` };
  }
  return { title: 'NEW PR', prev: `prev ${pr.prev.weight}kg` };
}

/* "80 × 5,5,5,4" when every set has the same weight, else the usual shape. */
function lastLine(sets) {
  if (!sets || !sets.length) return '';
  const w = sets[0].weight;
  return sets.every(s => s.weight === w) ? `${fmtKg(w)} × ${sets.map(s => s.reps).join(',')}` : formatSetShape(sets);
}

function ProgressRing({ t, pct }) {
  const c = 2 * Math.PI * 19;
  return (
    <div style={{ width: 46, height: 46, position: 'relative', flexShrink: 0 }}>
      <svg width="46" height="46" viewBox="0 0 46 46" aria-hidden="true">
        <circle cx="23" cy="23" r="19" fill="none" stroke={t.divider} strokeWidth="4" />
        <circle cx="23" cy="23" r="19" fill="none" stroke={t.accent} strokeWidth="4" strokeLinecap="round"
          strokeDasharray={`${(c * pct) / 100} ${c}`} transform="rotate(-90 23 23)" />
      </svg>
      <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, color: t.textMuted }}>
        {pct}%
      </span>
    </div>
  );
}

const roundBtn = t => ({
  width: 36, height: 36, borderRadius: 18, border: 'none', background: 'transparent', color: t.textMuted,
  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', touchAction: 'manipulation',
});

/* ────────────────────────────────────────────────────────────────────────── */
/*  Today tab                                                                 */
/* ────────────────────────────────────────────────────────────────────────── */

const panelBtn = t => ({
  flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', height: 40,
  background: 'transparent', border: `1px solid ${t.cardBorder}`, borderRadius: 8,
  padding: '0 10px', color: t.textMuted, fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700,
  touchAction: 'manipulation',
});

const chip = (t, tone) => ({
  flexShrink: 0, fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', padding: '2px 6px', borderRadius: 4,
  whiteSpace: 'nowrap', textTransform: 'uppercase',
  ...(tone === 'amber' ? { background: 'rgba(245, 158, 11, 0.14)', color: '#f59e0b' } : { background: t.accentSoft, color: t.accent }),
});

/* Today's target under last session: "Next: 82.5 × 8 · all sets hit 12", or
 * a program's or Claude's sets. `onUse` (fill the rows) is only passed while
 * the rows are untouched, like Copy. */
function NextTarget({ t, target, onUse, onClear, divided }) {
  const rx = target.source === 'prescription';
  const straight = isStraight(target.sets) && target.sets.every(s => s.weight != null);
  const week = target.week;
  const why = rx ? null : [
    week && week.weekCount > 1 ? week.weekLabel : null,
    week && week.trainingMax != null ? `TM ${fmtKg(week.trainingMax)} kg` : null,
    target.note,
  ].filter(Boolean).join(' · ');
  return (
    <div style={{
      marginTop: divided ? 10 : 0, paddingTop: divided ? 10 : 0,
      borderTop: divided ? `1px dashed ${t.cardBorder}` : 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Target size={13} color={t.accent} style={{ flexShrink: 0 }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: t.textFaint, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Next</span>
        <span style={{ ...chip(t), minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {rx ? (target.label === 'Claude' ? 'From Claude' : 'Your target') : target.badge}
        </span>
        <span style={{ flex: 1 }} />
        {onUse && (
          <button onClick={onUse} title="Fill today's sets with the target" style={{ ...panelBtn(t), color: t.accent, borderColor: t.accentBorder }}>
            <Target size={12} /> Use target
          </button>
        )}
        {onClear && (
          <button onClick={onClear} title="Remove this target; the program or plan target takes over" style={panelBtn(t)}>
            Clear
          </button>
        )}
      </div>
      <div style={{ marginTop: 8 }}>
        {straight ? (
          <div style={{ fontSize: 13, color: t.text, lineHeight: 1.4 }}>
            <span style={{ fontFamily: FONT_MONO, fontWeight: 700 }}>{targetShape(target.sets)}</span>
            {target.sets.length > 1 && <span style={{ color: t.textMuted, fontFamily: FONT_MONO }}> · {target.sets.length} sets</span>}
            {why && <span style={{ color: t.textMuted }}> · {why}</span>}
          </div>
        ) : (
          <>
            <SetChips t={t} sets={target.sets} />
            {why && <div style={{ fontSize: 12, color: t.textMuted, marginTop: 6 }}>{why}</div>}
          </>
        )}
        {rx && target.note && (
          <div style={{ fontSize: 12, color: t.text, marginTop: 6, lineHeight: 1.45, fontStyle: 'italic' }}>“{target.note}”</div>
        )}
      </div>
    </div>
  );
}

/* What the last session of this exercise actually looked like, set by set,
 * and today's target under it. `onCopy` is only passed when today's rows
 * are still untouched, so filling them in can never overwrite numbers
 * already entered. */
function PreviousSessionPanel({ t, prev, todayKeyStr, planDayName, onCopy, target, onUseTarget, onClearTarget }) {
  const ago = prev ? daysBetweenKeys(prev.date, todayKeyStr) : null;
  const when = !prev ? '' : ago === null ? prev.date
    : ago === 0 ? 'today'
    : ago === 1 ? 'yesterday'
    : `${ago} days ago`;
  const d = prev ? parseAnchor(prev.date) : null;
  const volume = prev ? sessionVolume(prev.sets) : 0;

  return (
    <div style={{
      marginTop: 12, padding: '10px 12px', borderRadius: 10,
      background: t.surfaceAlt, border: `1px solid ${t.divider}`,
    }}>
      {prev && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <History size={13} color={t.textFaint} style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: t.textFaint, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                Last time
              </span>
              <span style={{ fontSize: 11, color: t.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {when}{d ? ` · ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}
                {prev.sameDay && planDayName ? ` · ${planDayName}` : ''}
              </span>
            </div>
            {onCopy && (
              <button onClick={onCopy} title="Fill today's sets with these numbers" style={panelBtn(t)}>
                <Copy size={12} /> Copy
              </button>
            )}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            {prev.sets.map((set, i) => (
              <span key={i} style={{
                display: 'inline-flex', alignItems: 'baseline', gap: 5,
                padding: '3px 8px', borderRadius: 6,
                background: t.cardBg, border: `1px solid ${t.cardBorder}`,
              }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: t.textFaint, fontFamily: FONT_MONO }}>{i + 1}</span>
                <span style={{ fontSize: 12, fontFamily: FONT_MONO, fontWeight: 700, color: t.text }}>
                  {set.weight || 0}<span style={{ fontWeight: 400, color: t.textMuted }}>kg</span> × {set.reps || 0}
                </span>
              </span>
            ))}
          </div>

          <div style={{ fontSize: 10, color: t.textFaint, fontFamily: FONT_MONO, marginTop: 6 }}>
            {prev.sets.length} set{prev.sets.length === 1 ? '' : 's'}
            {volume > 0 ? ` · ${Math.round(volume).toLocaleString('en-GB')} kg volume` : ''}
            {!prev.sameDay && planDayName ? ' · from a different plan day' : ''}
          </div>
        </>
      )}
      {target && <NextTarget t={t} target={target} onUse={onUseTarget} onClear={onClearTarget} divided={!!prev} />}
    </div>
  );
}

/* The rest timer lives in the root (RestPill on every tab): this tab only
 * starts it, through onRestStart, and moves its snackbar above it. */
export function TodayTab({ t, state, setState, isDark, restActive, onRestStart, ui = 'classic' }) {
  const compact = ui === 'compact';
  const [checkinOpen, setCheckinOpen] = useState(false); // compact: the check-in behind its pill
  const [moreOpen, setMoreOpen] = useState({}); // compact: { [exerciseId]: bool } cues, note, last time, actions
  const [deloadSetup, setDeloadSetup] = useState(false);
  const [selectedOffset, setSelectedOffset] = useState(0);
  const [openExerciseId, setOpenExerciseId] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [swapTarget, setSwapTarget] = useState(null); // { ex, search: bool }: swap sheet, or the full picker
  const [programFor, setProgramFor] = useState(null); // { exercise, planExercise, planDayId }: Program sheet
  const [, bumpNudges] = useState(0); // re-render after "Not now" (stored in localStorage)
  const [tipsOpen, setTipsOpen] = useState({}); // { [exerciseId]: bool }
  // Snapshot at mount, so cues opened for the first time stay open this visit.
  const [tipsSeen] = useState(readTipsSeen);
  const [prToast, setPrToast] = useState(null); // { id, name, ...checkSetPr event }
  const [rirPrompt, setRirPrompt] = useState(null); // { key, name, index }: RIR strip after the last set
  const [setMenu, setSetMenu] = useState(null); // { ex, index }
  const [undo, setUndo] = useState(null); // { id, text, snap }
  const [autoReps, setAutoReps] = useState(null); // `${key}|${name}|${index}` of reps Done filled in
  const [needInput, setNeedInput] = useState(null); // { k: `${key}|${name}|${index}`, field: 'kg' | 'reps' } Done couldn't save
  const inputRefs = useRef({}); // `${name}#${index}#kg|reps` -> <input>, to focus a missing field
  const scrollToRef = useRef(null); // { id, block } of an element to scroll to after the next render
  const [finishOpen, setFinishOpen] = useState(false);
  const [dayPickerOpen, setDayPickerOpen] = useState(false);

  useEffect(() => {
    if (!prToast) return undefined;
    const handle = window.setTimeout(() => setPrToast(null), 4200);
    return () => window.clearTimeout(handle);
  }, [prToast]);

  useEffect(() => {
    if (!undo) return undefined;
    const handle = window.setTimeout(() => setUndo(null), 5000);
    return () => window.clearTimeout(handle);
  }, [undo]);

  useEffect(() => {
    if (!autoReps) return undefined;
    const handle = window.setTimeout(() => setAutoReps(null), 5000);
    return () => window.clearTimeout(handle);
  }, [autoReps]);

  // After a tick the next set (or the next exercise) moves; bring it into
  // view once the new layout is rendered.
  useEffect(() => {
    const target = scrollToRef.current;
    if (!target) return;
    scrollToRef.current = null;
    const el = document.getElementById(target.id);
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: target.block });
  });

  const date = new Date(workoutToday()); date.setDate(date.getDate() + selectedOffset);
  const key = dateKey(date);
  // The plan day this date was (or is being) trained as: its sessions stamp
  // first, then the cycle, so moving the cycle never relabels the past.
  const pd = dayFor(state, key);
  const planIdx = pd ? pd.index : 0;
  const workout = pd ? pd.day : state.plan[0];
  const dayLog = state.logs[key] || {};
  const session = state.sessions?.[key] || null;
  const trainDay = (day) => { setState(prev => chooseDay(prev, key, day)); setDayPickerOpen(false); setOpenExerciseId(null); };
  // A deload's sessions are left out of trends, progression and "last time"
  // (while deloading, last time may be the previous deload session).
  const deload = deloadOn(state, key);
  const trainLogs = trainingLogs(state, null);
  const histLogs = deload ? state.logs : trainLogs;

  /* Prefill today's rows from the last session. Only offered when nothing has
   * been entered for the exercise yet (see hasInput below), so this can't
   * clobber numbers already typed. Sets are left unticked — it's a starting
   * point, not a claim you did the work. */
  const copyPreviousSession = (exerciseName, prevSets) => setState(prev => {
    const log = { ...(prev.logs[key] || {}) };
    log[exerciseName] = prevSets.map(set => ({ weight: set.weight, reps: set.reps, done: false }));
    return { ...prev, logs: { ...prev.logs, [key]: log } };
  });

  /* "Use target": the same untouched guard and unticked rows as Copy. */
  const fillTarget = (exerciseName, targetSets) => setState(prev => {
    const log = { ...(prev.logs[key] || {}) };
    log[exerciseName] = fillFromTarget(targetSets);
    return { ...prev, logs: { ...prev.logs, [key]: log } };
  });

  const updateSet = (exerciseName, setIndex, patch) => {
    setState(prev => writeSet(prev, key, exerciseName, setIndex, patchSet(readSet(prev, key, exerciseName, setIndex), patch)));
  };

  const addSet = (exercise) => setState(prev => addSetTo(prev, key, exercise));

  // Removal keeps a snapshot of the two paths it touches for the Undo snackbar.
  const removeSet = (exercise, setIndex) => {
    const snap = snapshotExercise(state, key, exercise);
    setState(prev => removeSetFrom(prev, key, exercise, setIndex));
    setUndo({ id: Date.now(), text: `Set ${setIndex + 1} removed`, snap });
    setRirPrompt(null);
    setAutoReps(null);
    setSetMenu(null);
  };

  // Skip a planned exercise today (or remove an added one), with Undo.
  const skipToday = (exercise) => {
    const snap = snapshotExercise(state, key, exercise);
    setState(prev => skipExercise(prev, key, exercise));
    setUndo({ id: Date.now(), text: exercise.planned ? `${exercise.name} skipped today` : `${exercise.name} removed`, snap });
    setRirPrompt(null);
    if (openExerciseId === exercise.id) setOpenExerciseId(null);
  };

  const setRir = (exerciseName, setIndex, rir) => {
    setState(prev => writeSet(prev, key, exerciseName, setIndex, withRir(readSet(prev, key, exerciseName, setIndex), rir)));
  };

  const updateExerciseNote = (exerciseId, note) => {
    setState(prev => ({
      ...prev,
      plan: prev.plan.map(d => ({
        ...d,
        exercises: d.exercises.map(e => e.id === exerciseId ? { ...e, note } : e),
      })),
    }));
  };

  const addAdhocExercise = (name) => {
    setState(prev => {
      const log = { ...(prev.logs[key] || {}) };
      if (!log[name]) log[name] = [{}];
      return { ...prev, logs: { ...prev.logs, [key]: log } };
    });
    setOpenExerciseId(`adhoc-${name}`);
  };

  const daySwaps = state.swaps?.[key] || {};
  const customEx = makeCustomExerciseHandlers(setState);

  const swapExercise = (exerciseId, originalName, newName) => {
    const trimmed = (newName || '').trim();
    if (!trimmed) return;
    setState(prev => {
      const swaps = { ...(prev.swaps || {}) };
      const daySw = { ...(swaps[key] || {}) };
      if (trimmed === originalName) {
        delete daySw[exerciseId];
      } else {
        daySw[exerciseId] = trimmed;
      }
      if (Object.keys(daySw).length === 0) delete swaps[key];
      else swaps[key] = daySw;
      return { ...prev, swaps };
    });
    setOpenExerciseId(exerciseId);
  };

  const revertSwap = (exerciseId) => {
    setState(prev => {
      const swaps = { ...(prev.swaps || {}) };
      const daySw = { ...(swaps[key] || {}) };
      delete daySw[exerciseId];
      if (Object.keys(daySw).length === 0) delete swaps[key];
      else swaps[key] = daySw;
      return { ...prev, swaps };
    });
  };

  const planNames = new Set(workout.exercises.map(e => daySwaps[e.id] || e.name));
  const adhocNames = Object.keys(dayLog).filter(n => !planNames.has(n));
  const exercisesToShow = [
    ...workout.exercises.map(e => {
      const swappedName = daySwaps[e.id];
      const name = swappedName || e.name;
      // A swap keeps the slot's sets and rep range, but not its load step.
      const targetPe = swappedName ? { ...e, name, increment: undefined } : e;
      const target = cardTarget(state, key, name, targetPe);
      // A program, prescription or deload sets the day's set count (5/3/1 BBB: 8 sets on a 3-set slot).
      const fixed = target.shown && (target.source !== 'progression' || !!target.deload);
      return {
        id: e.id,
        name,
        originalName: e.name,
        swapped: !!swappedName,
        sets: fixed ? target.sets.length : e.sets, reps: e.reps, note: e.note, planned: true, planExercise: e,
        targetPe, target,
      };
    }),
    ...adhocNames.map(name => ({
      id: `adhoc-${name}`, name, originalName: name, swapped: false, sets: 0, reps: 0, note: '', planned: false,
      target: cardTarget(state, key, name),
    })),
  ];

  // Per-card numbers, computed once so the Done handler can see every card
  // (to find the next unfinished exercise).
  const rows = exercisesToShow.map(ex => {
    const sets = dayLog[ex.name] || [];
    const targetSets = ex.planned ? plannedCount(state, key, ex) : (ex.sets || Math.max(sets.length, 3));
    const totalRows = Math.max(ex.planned ? targetSets : 1, sets.length);
    const previous = findPreviousSession(histLogs, key, ex.name, { state, planDayId: workout.id });
    const completedHere = sets.filter(s => s?.done).length;
    // Up to yesterday, so ticking today's sets never flips the chip mid-session;
    // since the block start, so a new rep scheme does not read as a drop.
    const block = activeProgram(state.programs, ex.name, key, { planDayId: ex.planned ? workout.id : undefined, plan: state.plan });
    const stall = deload ? null : stallChip(liftTrend(trainLogs, ex.name, {
      today: addDays(key, -1), goal: state.profile?.goal, since: block?.startDate, exerciseMeta: state.exerciseMeta,
      stallSessions: planDaysWith(state, ex.originalName).length > 1 ? 4 : 3,
    }));
    return {
      ex, sets, targetSets, totalRows, previous,
      prevSets: previous ? previous.sets : [],
      completedHere, stall,
      exDone: ex.planned ? completedHere >= targetSets : completedHere > 0,
    };
  });

  /* The ghost of a row: what it shows greyed out and what Done saves into an
   * empty field. Today's target (Claude, program, plan progression) first. */
  const ghostFor = (row, i) => placeholderFor({
    sets: row.sets, prevSets: row.prevSets, planReps: row.ex.planned ? row.ex.reps : null,
    target: row.ex.target?.shown ? row.ex.target.sets : null,
  }, i);

  const refKey = (name, i, field) => `${name}#${i}#${field}`;
  const bindRef = (name, i, field) => (el) => {
    if (el) inputRefs.current[refKey(name, i, field)] = el;
    else delete inputRefs.current[refKey(name, i, field)];
  };
  // Done with a field it can't fill in: focus it and say so, so the tap never
  // looks like it did nothing (e.g. the first time on an exercise, no weight).
  const focusInput = (name, i, field) => {
    const el = inputRefs.current[refKey(name, i, field)];
    if (el) el.focus();
    setNeedInput({ k: `${key}|${name}|${i}`, field });
  };

  const toggleDone = (row, i) => {
    const { ex } = row;
    const setData = row.sets[i] || {};
    if (setData.done) {
      setState(prev => writeSet(prev, key, ex.name, i, untickSet(readSet(prev, key, ex.name, i))));
      setRirPrompt(null);
      return;
    }
    // Done saves what the row shows: typed values, else the ghost. Never a
    // done set without reps (or without weight on a loaded lift): ask instead.
    // An AMRAP set asks for the reps Mo got; its target is only the minimum.
    const ghost = ghostFor(row, i);
    if (ghost.amrap && (setData.reps == null || setData.reps === '')) { focusInput(ex.name, i, 'reps'); return; }
    const r = resolveDone(setData, ghost);
    if (r.reps === null) { focusInput(ex.name, i, 'reps'); return; }
    if (r.weight === null && !isBodyweight(ex.name)) { focusInput(ex.name, i, 'kg'); return; }
    const live = selectedOffset === 0; // times and rest only for live ticks, not backfills
    const now = live ? Date.now() : null;
    // The tick also stamps which plan day this date is, and times a live session.
    setState(prev => stampSession(writeSet(prev, key, ex.name, i,
      tickSet(readSet(prev, key, ex.name, i), { weight: r.weight, reps: r.reps, now })), key, { day: workout, now }));
    // Rest on every tick, also on another day: Mo sometimes trains yesterday's
    // missed session now and logs it there. Only the timestamps stay live-only.
    const nextSet = upNextSet(rows, rows.indexOf(row), i, r, ghostFor);
    if (nextSet) {
      unlockAudio(); // inside the tap: the only moment iOS lets a page start audio
      onRestStart({
        sec: restSecondsFor(ex.planned ? { ...ex.planExercise, name: ex.name } : { name: ex.name, reps: r.reps }),
        exercise: ex.name, planExId: ex.planned ? ex.id : null, nextSet,
      });
    }
    setAutoReps(r.autoReps ? `${key}|${ex.name}|${i}` : null);
    const pr = checkSetPr(state.logs, ex.name, key, r.weight, r.reps, { setIndex: i, exerciseMeta: state.exerciseMeta });
    if (pr) setPrToast({ id: Date.now(), name: ex.name, ...pr });

    // Last planned set: ask for RIR, close this card, open the next one.
    const finished = ex.planned && row.targetSets > 0 && row.completedHere + 1 >= row.targetSets;
    if (!finished) {
      setRirPrompt(null);
      scrollToRef.current = { id: `fit-active-${ex.id}`, block: 'nearest' };
      return;
    }
    setRirPrompt({ key, name: ex.name, index: i });
    const list = rows.map(x => ({ id: x.ex.id, done: x.ex.id === ex.id || x.exDone }));
    const next = nextUnfinished(list, ex.id);
    setOpenExerciseId(next);
    // When the next card sits right below, scroll to the finished one: its
    // RIR strip and the next card are then both in view.
    const at = list.findIndex(x => x.id === ex.id);
    scrollToRef.current = { id: `fit-ex-${next && list[at + 1]?.id !== next ? next : ex.id}`, block: 'start' };
  };

  const openName = rows.find(row => row.ex.id === openExerciseId)?.ex.name;
  useEffect(() => {
    if (openName && getTipsForExercise(openName)) markTipsSeen(openName);
  }, [openName]);

  // Progress counts planned sets only, capped per exercise; extras are listed apart.
  const prog = plannedProgress(rows.map(r => ({ name: r.ex.name, planned: r.ex.planned, target: r.targetSets, done: r.completedHere })));
  const totalSets = prog.total;
  const completedSets = prog.done;
  const anySetDone = countCompletedSets(dayLog) > 0;
  const totalVolume = sumDayVolume(dayLog);
  const progress = prog.pct;
  const allDone = !workout.isRest && totalSets > 0 && completedSets >= totalSets;

  // The summary opens by itself once, when the last planned set is ticked.
  useEffect(() => {
    if (!allDone || selectedOffset !== 0 || session?.summarySeen) return;
    setFinishOpen(true);
    setState(prev => setSessionFields(prev, key, { summarySeen: true }));
  }, [allDone, selectedOffset, session?.summarySeen, key, setState]);

  const dayOptions = [-1, 0, 1].map(off => {
    const d = new Date(workoutToday()); d.setDate(d.getDate() + off);
    return { off, label: off === 0 ? 'Today' : off === -1 ? 'Yesterday' : 'Tomorrow', date: d };
  });

  const exportJson = () => {
    const payload = {
      exported_at: new Date().toISOString(),
      date: key,
      is_rest_day: !!workout.isRest,
      workout: workout.isRest ? null : {
        name: workout.name,
        focus: workout.focus,
        type: workout.type,
        exercises: exercisesToShow.map(ex => {
          const exSets = dayLog[ex.name] || [];
          const override = state.setCounts?.[key]?.[ex.id];
          return {
            id: ex.id,
            name: ex.name,
            planned: ex.planned,
            swapped_from: ex.swapped ? ex.originalName : undefined,
            target_sets: ex.planned ? (override ?? ex.sets) : null,
            target_reps: ex.planned ? ex.reps : null,
            target: ex.target?.shown
              ? { source: ex.target.source, label: ex.target.badge, sets: ex.target.sets, note: ex.target.note || undefined }
              : undefined,
            note: ex.note || undefined,
            sets: exSets.map((s, i) => ({
              set: i + 1,
              weight: Number(s?.weight) || 0,
              reps: Number(s?.reps) || 0,
              done: !!s?.done,
              rir: typeof s?.rir === 'number' ? s.rir : undefined,
            })),
          };
        }),
      },
      totals: workout.isRest ? null : {
        completed_sets: completedSets,
        planned_sets: totalSets,
        total_volume_kg: totalVolume,
        progress_pct: progress,
      },
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `workout-${key}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Workout clock + manual rest, on the live day of a training day. Rest by
  // hand uses the open exercise's rest time (else the first unfinished one).
  const restRow = rows.find(r => r.ex.id === openExerciseId) || rows.find(r => !r.exDone) || null;
  const manualRestSec = restRow
    ? restSecondsFor(restRow.ex.planned ? { ...restRow.ex.planExercise, name: restRow.ex.name } : { name: restRow.ex.name })
    : 90;
  const liveBar = selectedOffset <= 0 && !workout.isRest ? (
    <WorkoutBar t={t} startedAt={session?.startedAt} clock={selectedOffset === 0} restSec={manualRestSec} restActive={restActive}
      onRest={() => {
        unlockAudio(); // inside the tap, so the end beep may play on iOS
        onRestStart({ sec: manualRestSec, exercise: restRow?.ex.name, planExId: restRow?.ex.planned ? restRow.ex.id : null });
      }} />
  ) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {compact && (() => {
        const o = dayOptions.find(x => x.off === selectedOffset);
        const dateText = o.date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
        const go = off => { setSelectedOffset(off); setOpenExerciseId(null); };
        const minutes = sessionMinutes(session);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <button aria-label="Previous day" disabled={selectedOffset <= -1} onClick={() => go(selectedOffset - 1)}
                  style={{ ...roundBtn(t), opacity: selectedOffset <= -1 ? 0.3 : 1 }}><ChevronLeft size={18} /></button>
                <span style={{ fontSize: 14, fontWeight: 500, color: t.textMuted, minWidth: 0 }}>
                  {selectedOffset === 0 ? dateText : `${o.label} · ${dateText}`}
                </span>
                <button aria-label="Next day" disabled={selectedOffset >= 1} onClick={() => go(selectedOffset + 1)}
                  style={{ ...roundBtn(t), opacity: selectedOffset >= 1 ? 0.3 : 1 }}><ChevronRight size={18} /></button>
              </div>
              {selectedOffset <= 0 && (
                <button type="button" onClick={() => setCheckinOpen(v => !v)} style={{
                  height: 32, padding: '0 12px', borderRadius: 16, border: 'none', cursor: 'pointer',
                  background: checkinOpen ? t.accentSoft : t.cardBg, color: checkinOpen ? t.accent : t.textMuted,
                  display: 'flex', alignItems: 'center', gap: 6, fontFamily: FONT_BODY, fontSize: 12.5, fontWeight: 500,
                }}>
                  <HeartPulse size={14} /> Check-in
                </button>
              )}
            </div>
            {checkinOpen && selectedOffset <= 0 && (
              <CheckinCard t={t} defaultOpen checkin={state.recovery?.[key]} dateLabel={selectedOffset === 0 ? 'today' : 'yesterday'}
                onChange={patch => setState(prev => setCheckin(prev, key, patch))} />
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '0 4px' }}>
              <div style={{ minWidth: 0 }}>
                <h2 style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-0.02em', color: t.text, margin: 0, lineHeight: 1.1 }}>{workout.name}</h2>
                <div style={{ fontSize: 13, color: t.textMuted, marginTop: 3 }}>
                  {workout.isRest ? 'Rest day' : `${completedSets} of ${totalSets} sets${minutes ? ` · ${minutes} min` : ''}`}
                  {session?.override ? ' · chosen day' : ''}
                </div>
              </div>
              {!workout.isRest && <ProgressRing t={t} pct={progress} />}
            </div>
            {liveBar}
            {((!anySetDone && state.plan.length > 1) || !deload) && (
              <div style={{ padding: '0 4px' }}>
                <div style={{ display: 'flex', gap: 18 }}>
                  {!anySetDone && state.plan.length > 1 && (
                    <button type="button" onClick={() => setDayPickerOpen(o2 => !o2)} style={{
                      background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: t.accent,
                      fontFamily: FONT_BODY, fontSize: 13, fontWeight: 500, minHeight: 32,
                    }}>Do another day</button>
                  )}
                  {!deload && (
                    <button type="button" onClick={() => setDeloadSetup(v => !v)} style={{
                      background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: t.accent,
                      fontFamily: FONT_BODY, fontSize: 13, fontWeight: 500, minHeight: 32,
                    }}>Deload</button>
                  )}
                </div>
                {dayPickerOpen && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                    {state.plan.map(d => (
                      <button key={d.id} type="button" onClick={() => trainDay(d)} style={{
                        height: 36, padding: '0 12px', borderRadius: 18, border: 'none', cursor: 'pointer',
                        background: d.id === workout.id ? t.accentSoft : t.cardBg, color: d.id === workout.id ? t.accent : t.text,
                        fontFamily: FONT_BODY, fontSize: 13, fontWeight: 500,
                      }}>{d.name}</button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {!compact && (<>
      {/* Day selector */}
      <div style={{ display: 'flex', gap: 6 }}>
        {dayOptions.map(o => {
          const active = o.off === selectedOffset;
          return (
            <button key={o.off} onClick={() => { setSelectedOffset(o.off); setOpenExerciseId(null); }}
              style={{
                flex: 1, padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                background: active ? t.accentSoft : t.cardBg,
                border: `1px solid ${active ? t.accentBorder : t.cardBorder}`,
                color: active ? t.accent : t.text,
                fontFamily: FONT_BODY, fontWeight: 600, fontSize: 13,
                transition: 'all 0.15s',
              }}>
              {o.label}
              <div style={{ fontSize: 10, color: active ? t.accent : t.textFaint, marginTop: 2, fontWeight: 500 }}>
                {o.date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
              </div>
            </button>
          );
        })}
      </div>

      {selectedOffset <= 0 && (
        <CheckinCard t={t} checkin={state.recovery?.[key]} dateLabel={selectedOffset === 0 ? 'today' : 'yesterday'}
          onChange={patch => setState(prev => setCheckin(prev, key, patch))} />
      )}

      {/* Workout header */}
      <Card t={t} padding={20} className="fit-today-header">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <TypeDot type={workout.type} />
              <span style={{ fontSize: 11, color: t.textFaint, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Day {planIdx + 1} of {state.plan.length} · {workout.type}
              </span>
            </div>
            <h2 className="fit-today-header-title" style={{ fontSize: 28, fontWeight: 700, color: t.text, lineHeight: 1.1, margin: 0 }}>
              {workout.name}
            </h2>
            {workout.focus && (
              <div style={{ fontSize: 14, color: t.textMuted, marginTop: 6 }}>{workout.focus}</div>
            )}
            {(session?.override || (!anySetDone && state.plan.length > 1) || !deload) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                {session?.override && <span style={chip(t)}>CHOSEN DAY</span>}
                {!anySetDone && state.plan.length > 1 && (
                  <button type="button" onClick={() => setDayPickerOpen(o => !o)} style={panelBtn(t)}>
                    <Repeat size={12} /> Do another day
                  </button>
                )}
                {!deload && (
                  <button type="button" onClick={() => setDeloadSetup(v => !v)} style={panelBtn(t)}>Deload</button>
                )}
              </div>
            )}
          </div>
          {!workout.isRest && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: t.textFaint, fontWeight: 600, marginBottom: 2 }}>PROGRESS</div>
              <div className="fit-today-header-pct" style={{ fontSize: 22, fontFamily: FONT_MONO, fontWeight: 700, color: progress === 100 ? t.accent : t.text }}>
                {progress}%
              </div>
              <div style={{ fontSize: 11, color: t.textMuted }}>{completedSets}/{totalSets} sets</div>
            </div>
          )}
        </div>
        {dayPickerOpen && !anySetDone && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
            {state.plan.map(d => (
              <button key={d.id} type="button" onClick={() => trainDay(d)} style={{
                ...panelBtn(t), color: d.id === workout.id ? t.accent : t.text,
                borderColor: d.id === workout.id ? t.accentBorder : t.cardBorder,
              }}>
                {d.name}
              </button>
            ))}
          </div>
        )}
        {!workout.isRest && (
          <div style={{ marginTop: 14, height: 4, background: t.surfaceAlt, borderRadius: 999, overflow: 'hidden' }}>
            <div style={{ width: `${progress}%`, height: '100%', background: t.accent, transition: 'width 0.3s' }} />
          </div>
        )}
      </Card>
      </>)}

      {!compact && liveBar}

      {deload && (
        <DeloadBanner t={t} deload={deload} onEnd={() => setState(prev => endDeload(prev, key))} />
      )}
      {!deload && deloadSetup && (
        <DeloadStarter t={t} onCancel={() => setDeloadSetup(false)}
          onStart={opts => { setState(prev => startDeload(prev, key, opts, uid('deload'))); setDeloadSetup(false); }} />
      )}

      {/* Rest day */}
      {workout.isRest && (
        <Card t={t} padding={48} style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🌙</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: t.text, marginBottom: 6 }}>Rest Day</div>
          <div style={{ fontSize: 13, color: t.textMuted, maxWidth: 320, margin: '0 auto' }}>
            Recovery is part of training. Hydrate, eat, sleep.
          </div>
          {(() => {
            const next = nextTrainingDay(state.plan, planIdx);
            return next ? (
              <Button t={t} size="md" onClick={() => trainDay(next)} style={{ marginTop: 18, minHeight: 44 }}>
                Train anyway: {next.name}
              </Button>
            ) : null;
          })()}
        </Card>
      )}

      {/* Exercises */}
      {!workout.isRest && rows.map(row => {
        if (isSkipped(state, key, row.ex)) return null;
        const { ex, sets, targetSets, totalRows, previous, completedHere, exDone, stall } = row;
        const tg = ex.target;
        const subtitle = [
          !ex.planned ? 'Ad-hoc'
            : tg.shown && tg.source !== 'progression' ? `${targetSets} sets` : `${targetSets} × ${repRangeLabel(ex.targetPe)}`,
          tg.shown ? `→ ${targetShape(tg.sets)}` : previous ? `last ${formatSetShape(previous.sets)}` : null,
          ex.swapped ? `was ${ex.originalName}` : null,
        ].filter(Boolean).join('  ·  ');
        const setup = ex.planned ? ex.planExercise.setup : null;
        // "Untouched" = no weight, no reps, nothing ticked. Only then is it
        // safe to offer to fill the rows in.
        const hasInput = sets.some(set => set?.done || set?.weight != null || set?.reps != null);
        const isOpen = openExerciseId === ex.id;
        const activeRow = firstOpenRow(sets, totalRows);
        const rirIndex = rirPrompt && rirPrompt.key === key && rirPrompt.name === ex.name ? rirPrompt.index : -1;
        const rirStrip = (i) => {
          const s = sets[i] || {};
          return (
            <RirStrip t={t} value={s.rir}
              caption={`Set ${i + 1}${s.weight != null ? ` · ${s.weight} kg` : ''}${s.reps != null ? ` × ${s.reps}` : ''}`}
              onPick={v => setRir(ex.name, i, v)}
              onClose={() => setRirPrompt(null)} />
          );
        };

        return (
          <Card key={ex.id} id={`fit-ex-${ex.id}`} t={t} padding={0} style={{ overflow: 'hidden', scrollMarginTop: 112 }}>
            <button className="fit-ex-head" onClick={() => setOpenExerciseId(isOpen ? null : ex.id)}
              style={{
                width: '100%', padding: '16px 20px', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: 12, background: 'transparent',
                border: 'none', cursor: 'pointer', textAlign: 'left',
              }}>
              {compact ? (() => {
                const w = tg.shown ? tg.sets[0]?.weight : previous?.sets[0]?.weight;
                const sub = [
                  !ex.planned ? 'Extra' : `${targetSets} × ${repRangeLabel(ex.targetPe)}${w ? ` · ${fmtKg(w)} kg` : ''}`,
                  ex.swapped ? `was ${ex.originalName}` : null,
                ].filter(Boolean).join(' · ');
                const n = ex.planned ? targetSets : Math.max(completedHere, 1);
                return (
                  <>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="fit-ex-head-name" style={{ fontSize: 16, fontWeight: 600, color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ex.name}</div>
                      <div style={{ fontSize: 13, color: t.textMuted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</div>
                    </div>
                    <div aria-label={`${completedHere} of ${n} sets done`} style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      {Array.from({ length: Math.min(n, 8) }).map((_, i) => (
                        <span key={i} style={{ width: 7, height: 7, borderRadius: 4, background: i < completedHere ? t.accent : t.surfaceAlt }} />
                      ))}
                    </div>
                  </>
                );
              })() : (<>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 }}>
                <div style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: exDone ? t.accent : t.cardBorder, flexShrink: 0,
                }} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="fit-ex-head-name" style={{ fontSize: 15, fontWeight: 600, color: t.text, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{ex.name}</span>
                    {!ex.planned && <span style={chip(t)}>EXTRA</span>}
                    {ex.swapped && <span style={chip(t)}>SWAP</span>}
                    {stall && <span title={stall.title} aria-label={stall.title} style={chip(t, 'amber')}>{stall.text}</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, minWidth: 0 }}>
                    {tg.badge && <span style={{ ...chip(t), maxWidth: '55%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tg.badge}</span>}
                    <span style={{ fontSize: 12, color: t.textMuted, fontFamily: FONT_MONO, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {subtitle}
                    </span>
                  </div>
                  {setup && (
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 5, maxWidth: '100%',
                      padding: '2px 8px', borderRadius: 6, background: t.surfaceAlt, border: `1px solid ${t.cardBorder}`,
                      fontSize: 11, color: t.textMuted,
                    }}>
                      <Wrench size={10} color={t.textFaint} style={{ flexShrink: 0 }} />
                      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{setup}</span>
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12, fontFamily: FONT_MONO, fontWeight: 600, color: exDone ? t.accent : t.textMuted }}>
                  {ex.planned ? `${completedHere}/${targetSets}` : `${completedHere}`}
                </span>
                <ChevronDown size={16} color={t.textFaint} style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
              </div>
              </>)}
            </button>

            {/* Finished and closed: the RIR question stays until the next tick. */}
            {!isOpen && rirIndex >= 0 && (
              <div className="fit-ex-body" style={{ padding: '0 20px 4px' }}>{rirStrip(rirIndex)}</div>
            )}

            {isOpen && (
              <div className="fit-ex-body" style={{ padding: '0 20px 16px', borderTop: compact ? 'none' : `1px solid ${t.divider}` }}>
                {compact && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: t.textMuted, lineHeight: 1.4 }}>
                      {previous ? <>Last <b style={{ color: t.text, fontWeight: 600 }}>{lastLine(previous.sets)}</b></> : 'First time'}
                      {tg.shown && <> · today <b style={{ color: t.text, fontWeight: 600 }}>{targetShape(tg.sets)}</b></>}
                    </div>
                    <button type="button" aria-label="More options" aria-expanded={!!moreOpen[ex.id]}
                      onClick={() => setMoreOpen(m => ({ ...m, [ex.id]: !m[ex.id] }))}
                      style={{ ...roundBtn(t), background: moreOpen[ex.id] ? t.accentSoft : t.surfaceAlt, color: moreOpen[ex.id] ? t.accent : t.textMuted }}>
                      <MoreHorizontal size={16} />
                    </button>
                  </div>
                )}
                {(!compact || moreOpen[ex.id]) && (<>
                {/* Tips & Note */}
                {(() => {
                  const tips = getTipsForExercise(ex.name);
                  const tipsExpanded = tipsOpen[ex.id] ?? (!!tips && !tipsSeen[ex.name.toLowerCase()]);
                  if (!tips && !ex.planned) return null;
                  return (
                    <div style={{ paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {tips && (
                        <div style={{
                          background: t.surfaceAlt, borderRadius: 8, border: `1px solid ${t.cardBorder}`,
                          overflow: 'hidden',
                        }}>
                          <button onClick={() => setTipsOpen(p => ({ ...p, [ex.id]: !tipsExpanded }))}
                            style={{
                              width: '100%', padding: '8px 12px', display: 'flex', alignItems: 'center',
                              justifyContent: 'space-between', gap: 8,
                              background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
                            }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <Lightbulb size={14} color="#f59e0b" />
                              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: t.text }}>
                                Form cues
                              </span>
                              <span style={{ fontSize: 11, color: t.textFaint, fontWeight: 500 }}>
                                {tips.length} tips
                              </span>
                            </span>
                            <ChevronDown size={14} color={t.textFaint}
                              style={{ transform: tipsExpanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
                          </button>
                          {tipsExpanded && (
                            <ul style={{
                              listStyle: 'none', margin: 0, padding: '0 12px 12px',
                              display: 'flex', flexDirection: 'column', gap: 6,
                            }}>
                              {tips.map((tip, i) => (
                                <li key={i} style={{
                                  fontSize: 13, lineHeight: 1.45, color: t.text,
                                  display: 'flex', gap: 8, paddingLeft: 4,
                                }}>
                                  <span style={{ color: '#f59e0b', flexShrink: 0, marginTop: 1 }}>•</span>
                                  <span>{tip}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                      {ex.planned && (
                        <div style={{
                          display: 'flex', alignItems: 'flex-start', gap: 8, paddingTop: 2,
                        }}>
                          <StickyNote size={14} color={t.textFaint} style={{ marginTop: 10, flexShrink: 0 }} />
                          <textarea
                            value={ex.note || ''}
                            onChange={e => updateExerciseNote(ex.id, e.target.value)}
                            placeholder="Plan note (every session)…"
                            rows={1}
                            style={{
                              flex: 1, background: t.inputBg, color: t.text,
                              border: `1px solid ${t.inputBorder}`, borderRadius: 8,
                              padding: '8px 10px', fontSize: 12, fontFamily: FONT_BODY,
                              resize: 'vertical', minHeight: 32, outline: 'none',
                            }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })()}

                {(previous || tg.shown) && (
                  <PreviousSessionPanel
                    t={t} prev={previous} todayKeyStr={key}
                    planDayName={workout?.name}
                    onCopy={hasInput || !previous ? null : () => copyPreviousSession(ex.name, previous.sets)}
                    target={tg.shown ? tg : null}
                    onUseTarget={hasInput ? null : () => fillTarget(ex.name, tg.sets)}
                    onClearTarget={tg.source === 'prescription' ? () => setState(prev => clearPrescription(prev, key, ex.name)) : null} />
                )}
                </>)}

                {/* Set table: the first unfinished set is the big active panel,
                    the others stay compact rows. */}
                {!compact && <SetTableHeader t={t} />}
                {Array.from({ length: totalRows }).map((_, i) => {
                  const setData = sets[i] || {};
                  const ghost = ghostFor(row, i);
                  const rowKey = `${key}|${ex.name}|${i}`;
                  const onWeight = v => {
                    updateSet(ex.name, i, { weight: v });
                    if (needInput?.k === rowKey) setNeedInput(null);
                  };
                  const onReps = v => {
                    updateSet(ex.name, i, { reps: v });
                    if (needInput?.k === rowKey) setNeedInput(null);
                    if (autoReps === `${key}|${ex.name}|${i}`) setAutoReps(null);
                  };
                  const onMenu = () => setSetMenu({ ex, index: i });
                  return (
                    <Fragment key={i}>
                      {i === activeRow && !compact ? (
                        <ActiveSetPanel t={t} id={`fit-active-${ex.id}`} n={i + 1} set={setData} ghost={ghost}
                          kgStep={loadStep(ex.name, ex.targetPe, state.logs, state.exerciseMeta)}
                          onWeight={onWeight} onReps={onReps} onDone={() => toggleDone(row, i)} onMenu={onMenu}
                          weightRef={bindRef(ex.name, i, 'kg')} repsRef={bindRef(ex.name, i, 'reps')} />
                      ) : (
                        <CompactSetRow t={t} n={i + 1} set={setData} ghost={ghost} first={i === 0 || i === activeRow + 1}
                          active={compact && i === activeRow} id={compact && i === activeRow ? `fit-active-${ex.id}` : undefined}
                          autoReps={autoReps === `${key}|${ex.name}|${i}`}
                          onWeight={onWeight} onReps={onReps} onToggle={() => toggleDone(row, i)} onMenu={onMenu}
                          weightRef={bindRef(ex.name, i, 'kg')} repsRef={bindRef(ex.name, i, 'reps')} />
                      )}
                      {needInput?.k === rowKey && (
                        <div role="alert" style={{
                          margin: '2px 0 6px', padding: '8px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                          background: 'rgba(245, 158, 11, 0.14)', color: '#f59e0b',
                        }}>
                          {needInput.field === 'kg' ? 'Enter the weight (kg) first, then tap Done.' : 'Enter the reps first, then tap Done.'}
                        </div>
                      )}
                      {i === rirIndex && rirStrip(i)}
                    </Fragment>
                  );
                })}
                <div style={{ paddingTop: 10, display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button t={t} size="sm" onClick={() => addSet(ex)}>
                      <Plus size={13} /> Add set
                    </Button>
                    {totalRows > 0 && (
                      <Button t={t} size="sm" variant="ghost" onClick={() => removeSet(ex, totalRows - 1)}
                        aria-label={`Remove set ${totalRows}`}>
                        <Minus size={13} /> Remove set
                      </Button>
                    )}
                  </div>
                  {(!compact || moreOpen[ex.id]) && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Button t={t} size="sm" variant="ghost"
                      onClick={() => setProgramFor({ exercise: ex.name, planExercise: ex.planned ? ex.targetPe : null, planDayId: ex.planned ? workout.id : null })}>
                      <Layers size={13} /> Program
                    </Button>
                    {ex.planned && (
                      <Button t={t} size="sm" variant="ghost" onClick={() => setSwapTarget({ ex, search: false })}>
                        <Repeat size={13} /> Swap
                      </Button>
                    )}
                    {ex.planned && ex.swapped && (
                      <Button t={t} size="sm" variant="ghost" onClick={() => revertSwap(ex.id)}>
                        <RotateCcw size={13} /> Revert
                      </Button>
                    )}
                    <Button t={t} size="sm" variant="danger" onClick={() => skipToday(ex)}>
                      <Trash2 size={13} /> {ex.planned ? 'Skip today' : 'Remove'}
                    </Button>
                  </div>}
                </div>
                {ex.planned && (() => {
                  const nudge = swapNudge(state, ex.originalName, key);
                  if (!nudge || nudgeSnoozed(ex.originalName, nudge.name, key)) return null;
                  return (
                    <SwapNudge t={t} name={nudge.name} recent={nudge.recent}
                      onAccept={() => setState(prev => promoteSwap(prev, ex.id, nudge.name, key))}
                      onDismiss={() => { snoozeNudge(ex.originalName, nudge.name, key); bumpNudges(x => x + 1); }} />
                  );
                })()}
              </div>
            )}
          </Card>
        );
      })}

      {/* Planned exercises skipped today, with a way back */}
      {!workout.isRest && rows.filter(r => isSkipped(state, key, r.ex)).map(r => (
        <div key={`skipped-${r.ex.id}`} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '6px 14px',
          borderRadius: 10, border: `1px dashed ${t.cardBorder === 'transparent' ? t.divider : t.cardBorder}`,
        }}>
          <span style={{ fontSize: 13, color: t.textMuted, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Skipped today: {r.ex.name}
          </span>
          <Button t={t} size="sm" variant="ghost" style={{ minHeight: 40, flexShrink: 0 }}
            onClick={() => setState(prev => unskipExercise(prev, key, r.ex))}>
            <RotateCcw size={13} /> Restore
          </Button>
        </div>
      ))}

      {/* Add ad-hoc exercise */}
      {!workout.isRest && (
        <Button t={t} size="md" onClick={() => setPickerOpen(true)}
          style={{ alignSelf: 'flex-start' }}>
          <Plus size={14} /> Add exercise to today
        </Button>
      )}

      {pickerOpen && (
        <ExercisePicker t={t} isDark={isDark} aliases={state.exerciseAliases}
          customExercises={state.customExercises || []} {...customEx}
          onPick={addAdhocExercise}
          onClose={() => setPickerOpen(false)} />
      )}

      {swapTarget && !swapTarget.search && (
        <SwapSheet t={t} state={state} dateKey={key} ex={swapTarget.ex}
          todayNames={exercisesToShow.map(e => e.name).filter(n => n !== swapTarget.ex.name)}
          onSwap={(name) => swapExercise(swapTarget.ex.id, swapTarget.ex.originalName, name)}
          onSearch={() => setSwapTarget(st => ({ ...st, search: true }))}
          onClose={() => setSwapTarget(null)} />
      )}

      {programFor && (
        <ProgramSheet t={t} state={state} setState={setState} date={key} {...programFor}
          onClose={() => setProgramFor(null)} />
      )}

      {swapTarget && swapTarget.search && (
        <ExercisePicker t={t} isDark={isDark} autoFocus={false} aliases={state.exerciseAliases}
          currentName={swapTarget.ex.name} initialMuscle={libraryMuscle(swapTarget.ex.originalName)}
          customExercises={state.customExercises || []} {...customEx}
          onPick={(name) => swapExercise(swapTarget.ex.id, swapTarget.ex.originalName, name)}
          onClose={() => setSwapTarget(null)} />
      )}

      {!workout.isRest && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <StatTile t={t} label="Total Volume" value={`${totalVolume.toLocaleString()} kg`} />
          <StatTile t={t} label="Sets Done" value={`${completedSets}/${totalSets}`} accent={completedSets === totalSets && totalSets > 0} />
        </div>
      )}

      {/* Session summary: replaces the old export row (export lives inside it) */}
      {(!workout.isRest || anySetDone) && (
        <FinishCard t={t} open={finishOpen} onToggle={() => setFinishOpen(o => !o)}
          minutes={sessionMinutes(session)} progress={prog} volume={totalVolume}
          lastSame={finishOpen ? lastSameDay(state, key, workout.id) : null}
          prs={finishOpen ? prsOn(state, key) : []}
          session={session} onSession={patch => setState(prev => setSessionFields(prev, key, patch))}
          onExport={exportJson} />
      )}

      {/* PR toast */}
      {prToast && (
        <div key={prToast.id} style={{
          position: 'fixed', top: 'max(env(safe-area-inset-top), 18px)', left: '50%',
          transform: 'translateX(-50%)', zIndex: 60, pointerEvents: 'none',
          animation: 'prToastIn 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) both',
        }}>
          <div style={{
            position: 'relative',
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '12px 18px 12px 14px', borderRadius: 14,
            background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 55%, #ea580c 100%)',
            color: '#fff', fontFamily: FONT_BODY,
            border: '1px solid rgba(255,255,255,0.25)',
            animation: 'prGlow 1.6s ease-in-out infinite',
            minWidth: 240,
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'rgba(255,255,255,0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              animation: 'prTrophySpin 0.9s ease-out both',
            }}>
              <Trophy size={20} color="#fff" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
              <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', opacity: 0.92 }}>
                {prLines(prToast).title}
              </span>
              <span style={{ fontSize: 16, fontWeight: 700, marginTop: 2, fontFamily: FONT_MONO }}>
                {prToast.set.weight}kg × {prToast.set.reps}
              </span>
              <span style={{ fontSize: 11, opacity: 0.88, marginTop: 2, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {prToast.name} · {prLines(prToast).prev}
              </span>
            </div>
            {[
              { '--sparkle-end': 'translate(-22px, -18px)', top: 6, left: 4, delay: '0.05s' },
              { '--sparkle-end': 'translate(24px, -20px)', top: 4, right: 8, delay: '0.18s' },
              { '--sparkle-end': 'translate(28px, 22px)', bottom: 6, right: 4, delay: '0.30s' },
              { '--sparkle-end': 'translate(-26px, 20px)', bottom: 4, left: 6, delay: '0.42s' },
            ].map((s, idx) => {
              const { delay, ...rest } = s;
              return (
                <span key={idx} style={{
                  position: 'absolute', width: 6, height: 6, borderRadius: '50%',
                  background: '#fff',
                  animation: `prSparkle 0.9s ease-out ${delay} both`,
                  ...rest,
                }} />
              );
            })}
          </div>
        </div>
      )}

      {setMenu && (() => {
        const { ex, index } = setMenu;
        return (
          <SetMenuSheet t={t} n={index + 1} name={ex.name} set={dayLog[ex.name]?.[index] || {}}
            onRir={v => { setRir(ex.name, index, v); setSetMenu(null); }}
            onRemove={() => removeSet(ex, index)}
            onClose={() => setSetMenu(null)} />
        );
      })()}

      {undo && (
        <UndoSnackbar key={undo.id} t={t} text={undo.text}
          bottom={restActive ? 'calc(max(env(safe-area-inset-bottom), 16px) + 76px)' : 'max(env(safe-area-inset-bottom), 24px)'}
          onUndo={() => { setState(prev => restoreExercise(prev, undo.snap)); setUndo(null); }} />
      )}
    </div>
  );
}
